import { useEffect, useRef } from "react";
import { offerings, steps, email, emailText, headshot, tagline, belief, about, contact, socials, year } from "./content";
import Switcher from "./Switcher";
import "./bitmap.css";

// Tiny pixel glyphs drawn on an 8x8 grid: each string row is one line, '#' is a lit cell.
const glyphs = {
  "01": ["..####..", ".#....#.", "#.#..#.#", "#......#", "#.####.#", "#......#", ".#....#.", "..####.."],
  "02": ["########", "#......#", "#.####.#", "#.#..#.#", "#.####.#", "#......#", "#......#", "########"],
  "03": ["#.......", "##......", "###.....", "####....", "#####...", "###.....", "#.##....", "...##..."],
};

function Glyph({ id }) {
  return (
    <div className="d4-glyph" aria-hidden="true">
      {glyphs[id].map((row, y) => row.split("").map((cell, x) => <i key={`${x}${y}`} className={cell === "#" ? "on" : ""} />))}
    </div>
  );
}

function PixelPortrait() {
  const canvas = useRef(null);
  useEffect(() => {
    const el = canvas.current;
    if (!el || process.env.NODE_ENV === "test") return;
    const ctx = el.getContext("2d");
    if (!ctx) return;
    const img = new Image();
    img.onload = () => {
      const cells = 56;
      const small = document.createElement("canvas");
      small.width = cells;
      small.height = cells;
      const sctx = small.getContext("2d");
      sctx.drawImage(img, 0, 0, cells, cells);
      const data = sctx.getImageData(0, 0, cells, cells);
      for (let i = 0; i < data.data.length; i += 4) {
        const l = 0.299 * data.data[i] + 0.587 * data.data[i + 1] + 0.114 * data.data[i + 2];
        const level = Math.round(l / 64) / 4; // four tones
        data.data[i] = 17 + level * 238; // charcoal → amber-ish tint
        data.data[i + 1] = 17 + level * 163;
        data.data[i + 2] = 17 + level * 30;
      }
      sctx.putImageData(data, 0, 0);
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(small, 0, 0, el.width, el.height);
    };
    img.src = headshot;
  }, []);
  return (
    <div className="d4-portrait">
      <canvas ref={canvas} width="448" height="448" aria-hidden="true" />
      <img src={headshot} alt={about.alt} width="448" height="448" loading="lazy" />
    </div>
  );
}

export default function Bitmap() {
  return (
    <div className="d4" id="top">
      <a className="d4-skip" href="#main">Skip to content</a>

      <header className="d4-header d4-wrap">
        <a className="d4-brand" href="#top" aria-label="Bit Haiku home">BIT HAIKU</a>
        <nav aria-label="Main navigation">
          <a href="#services">Services</a>
          <a href="#about">About</a>
          <a href="#contact">Contact</a>
        </nav>
      </header>

      <main id="main">
        <section className="d4-hero d4-wrap" aria-labelledby="d4-hero-title">
          <div>
            <p className="d4-label">&gt; software / ai automation / security</p>
            <h1 id="d4-hero-title">More business.<br />Less busywork.<span className="d4-cursor" aria-hidden="true" /></h1>
            <p className="d4-lead">{tagline}</p>
            <a className="d4-btn" href="#contact">Press start</a>
          </div>
          <aside className="d4-panel" aria-label="How a project goes">
            <div className="d4-panel-bar"><span>how_a_project_goes</span><span aria-hidden="true">□ ×</span></div>
            <ol>
              {steps.map((step, i) => (
                <li key={step.id}>
                  <div className="d4-step-head"><span>{step.label}</span><span aria-hidden="true">{"█".repeat(i + 1).padEnd(3, "░")}</span></div>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </li>
              ))}
            </ol>
          </aside>
        </section>

        <section className="d4-services d4-wrap" id="services" aria-labelledby="d4-services-title">
          <p className="d4-label">&gt; what we do</p>
          <h2 id="d4-services-title">Remove the pain from your processes.</h2>
          <div className="d4-tiles">
            {offerings.map((offering) => (
              <article key={offering.id} className="d4-tile">
                <Glyph id={offering.id} />
                <h3>{offering.title}</h3>
                <p>{offering.text}</p>
                <ul>{offering.items.map((item) => <li key={item}>{item}</li>)}</ul>
              </article>
            ))}
          </div>
        </section>

        <section className="d4-belief" aria-labelledby="d4-belief-title">
          <div className="d4-wrap d4-belief-card">
            <p className="d4-label">&gt; how we work</p>
            <h2 id="d4-belief-title">{belief.title}</h2>
            {belief.paragraphs.map((text) => <p key={text}>{text}</p>)}
          </div>
        </section>

        <section className="d4-about d4-wrap" id="about" aria-labelledby="d4-about-title">
          <PixelPortrait />
          <div>
            <p className="d4-label">&gt; player 1: {about.name}, {about.role.toLowerCase()}</p>
            <h2 id="d4-about-title">{about.title}</h2>
            {about.paragraphs.map((text) => <p key={text} className="d4-copy">{text}</p>)}
            <a className="d4-btn d4-btn-ghost" href="#contact">{about.cta}</a>
          </div>
        </section>

        <section className="d4-contact" id="contact" aria-labelledby="d4-contact-title">
          <div className="d4-wrap">
            <p className="d4-label">&gt; get in touch</p>
            <h2 id="d4-contact-title">What would simplicity feel like?</h2>
            <div className="d4-contact-row">
              <p>{contact.lines.map((line) => <span key={line}>{line}<br /></span>)}</p>
              <a className="d4-email" href={email}>{emailText}</a>
            </div>
          </div>
        </section>
      </main>

      <footer className="d4-footer d4-wrap">
        <span>© {year} Bit Haiku LLC</span>
        <div>{socials.map((s) => <a key={s.label} href={s.href} target="_blank" rel="noreferrer">[{s.label}]</a>)}</div>
      </footer>
      <Switcher current={4} />
    </div>
  );
}
